/**
 * Hosts the realtime channel for the signed-in session (TO-468, ADR-0049): one
 * connection while the build is configured and the session has both a userId and
 * a tenantId; stopped on logout/unmount. Registers the query and notification
 * bridges on the dispatcher. With no config the state is DISABLED and every
 * screen keeps polling.
 */
import { useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { pushNotification } from '@/notifications/notification-store'
import { createNotificationBridge } from './notification-bridge'
import { createQueryBridge } from './query-bridge'
import { type RealtimeConfig, realtimeConfig } from './realtime-config'
import { type RealtimeConnectionDeps, startRealtimeConnection } from './realtime-connection'
import {
	RealtimeConnectionState,
	setRealtimeConnectionState,
	subscribeRealtime,
	subscribeRealtimeConnectionState,
} from './realtime-dispatcher'
import { getRealtimeToken } from './realtime-token'

export interface RealtimeProviderProps {
	children: ReactNode
	config?: RealtimeConfig | null
	start?: (deps: RealtimeConnectionDeps) => () => void
}

export function RealtimeProvider({
	children,
	config = realtimeConfig,
	start = startRealtimeConnection,
}: RealtimeProviderProps) {
	const { user } = useAuth()
	const queryClient = useQueryClient()
	const userId = user?.userId
	const tenantId = user?.tenantId

	useEffect(() => {
		if (!config) {
			setRealtimeConnectionState(RealtimeConnectionState.DISABLED)
			return
		}
		if (!userId || !tenantId) return

		const queryBridge = createQueryBridge(queryClient)
		const unsubscribers = [
			subscribeRealtime({}, queryBridge.onMessage),
			subscribeRealtimeConnectionState(queryBridge.onStateChange),
			subscribeRealtime({}, createNotificationBridge(pushNotification)),
		]
		const stop = start({ config, tenantId, userId, getToken: getRealtimeToken })
		return () => {
			stop()
			for (const unsubscribe of unsubscribers) unsubscribe()
			queryBridge.dispose()
		}
	}, [config, userId, tenantId, queryClient, start])

	return <>{children}</>
}
